import React from 'react';
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "react-query";
import * as Yup from 'yup';
import countries from '../../config/countries';
import { renderRailsErrors } from "../../utils/rails_error_parser";

import {
  TextInput,
  TextAreaInput,
  SelectInput,
  NestedSelectInput,
  Base64FileInput,
} from "./../react-hook-form-inputs";
import {
  updateCompany
} from "./../../queries/company";
import { useSectorsList } from "./../../queries/staticdata";

import "../components.Common.scss";

const MAX_LOGO_FILE_SIZE = 200 * 1024;
const MAX_BANNER_FILE_SIZE = 10 * 1024;
const SUPPORTED_FORMATS = "image/jpg,image/jpeg,image/gif,image/png";

const CompanySchema = Yup.object().shape({
  logo: Yup.mixed(),

  banner: Yup.mixed(),

  name: Yup.string()
    .required("Company name is required")
    .max(255, "Name too long, max-length 255 characters"),

  legal_name: Yup.string()
    .required("Company's legal name is required")
    .max(255, "Company legal name too long, max-length 255 characters"),

  private: Yup.boolean()
    .required("Company type is required"),

  sector_id: Yup.number()
    .required("Sector is required"),

  twitter_url: Yup.string().nullable()
  .typeError("")
  .transform(value => !value ? null : value)
  .matches( /http(?:s)?:\/\/(?:www\.)?twitter\.com\/([a-zA-Z0-9_]+)/, 'enter valid url!' ),

  linkedin_url: Yup.string().nullable()
  .typeError("")
  .transform(value => !value ? null : value)
  .matches( /http(?:s)?:\/\/(?:www\.)?linkedin\.com\/([a-zA-Z0-9_]+)/, 'enter valid url!' )

});

function EditForm({company={}}) {
  const queryClient = useQueryClient();
  const {data:sectorsList } = useSectorsList()

  const defaultValues = Object.assign({
    logo: '',
    banner: '',
    name: '',
    legal_name: '',
    url: '',
    private: '',
    employee_count: '',
    founding_year: '',
    company_type: '',
    last_funding_type: '',
    sector_id: '',
    tagline: '',
    description: '',
    city: '',
    state: '',
    country: '',
    twitter_url: '',
    linkedin_url: '',
  }, company)
  
  const updateCompanyMutation = useMutation(
    updateCompany,
    {
      onSuccess: (updatedCompany) => {
        toast.success('Company updated.')
        queryClient.setQueryData(
          ["company", company.slug],
          updatedCompany
        )
      },
      onError: (error) => {
        toast.error('Company update failed.')
        renderRailsErrors(error)
      },
    }
  )

  const methods = useForm({
    resolver: yupResolver(CompanySchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    shouldUseNativeValidation: false,
    criteriaMode: "firstError",
    defaultValues: defaultValues,
  })

  React.useEffect(
    () => {
      methods.reset(company)
    },
    [company, methods]
  )

  const onSubmit = (values) => {
    updateCompanyMutation.mutate({
      company_id: company.slug,
      values: {company: values},
    })
  }

  return(
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>


        <div className="row mb-3">
          <div className="col-sm-6">
            <Base64FileInput
              name="logo"
              placeholder="Upload Logo"
              accept={SUPPORTED_FORMATS}
              prevUrl={company?.logo_url}
              containerSizingClass="logo-square-90"
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-sm-6">
            <Base64FileInput
              name="banner"
              placeholder="Upload Banner"
              accept={SUPPORTED_FORMATS}
              prevUrl={company?.banner_url}
              containerSizingClass="banner"
            />
          </div>
        </div>

        <div className="row">
          <div className="col-sm-6">
            <TextInput
              name="name"
              placeholder="Name"
            />
          </div>
          <div className="col-sm-6">
            <TextInput
              name="legal_name"
              placeholder="Legal name"
            />
          </div>
        </div>

        <div className="row">
          <div className="col-sm-6">
            <TextInput
              name="url"
              placeholder="Website"
            />
          </div>
          <div className="col-sm-6">
            <SelectInput
              name="private"
              placeholder="Private or Public company"
            >
              <option value="">Select company type</option>
              <option value={true}>Private</option>
              <option value={false}>Public</option>
            </SelectInput>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-6">
            <TextInput
              name="employee_count"
              placeholder="Employee count"
            />
          </div>
          <div className="col-sm-6">
            <TextInput
              name="founding_year"
              placeholder="Founding year"
            />
          </div>
        </div>


        <div className="row">
          <div className="col-sm-6">
            <NestedSelectInput
              name='sector_id'
              placeholder='Sector & Subsector'
              options={sectorsList}
              childrenNestingKey='children'
              parentOptionLabel='name'
              parentOptionValue='id'
              optionLabel='name'
              optionValue='id'
              parentSelectable={false}
            />

          </div>
          <div className="col-sm-6">
            <SelectInput
              name="last_funding_type"
              placeholder="Last funding stage"
            >
              <option value="">Select last funding type</option>
              <option value="Pre-Seed">Pre-Seed</option>
              <option value="Seed">Seed</option>
              <option value="Series A">Series A</option>
              <option value="Series B">Series B</option>
              <option value="Series C">Series C</option>
              <option value="Series C+">Series C+</option>
              <option value="Grant">Grant</option>
              <option value="IPO">IPO</option>
              <option value="Undisclosed">Undisclosed</option>
              <option value="None">None</option>
            </SelectInput>
          </div>
        </div>

        <div className="row">
          <div className="col">
            <TextInput
              name="tagline"
              placeholder="Tagline"
            />
          </div>
        </div>

        <div className="row">
          <div className="col">
            <TextAreaInput
              name="description"
              placeholder="Description"
            />
          </div>
        </div>

        <div className="row">
          <div className="col-sm-6">
            <TextInput
              name="city"
              placeholder="City"
            />
          </div>
          <div className="col-sm-6">
            <SelectInput
              name="country"
              placeholder="Country"
            >
              <option value="">Select Country</option>
              {
                countries.map(c => <option value={c} key={c}>{c}</option>)
              }
            </SelectInput>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-6">
            <TextInput
              name="linkedin_url"
              placeholder="LinkedIn url"
            />
          </div>
          <div className="col-sm-6">
            <TextInput
              name="twitter_url"
              placeholder="Twitter url"
            />
          </div>
        </div>


        <div className="form-group d-flex">
          <button type="submit"
            className="ms-auto btn btn-outline-dark rounded-0 submit-btn"
            disabled={methods.isSubmitting}
          >
            Save
          </button>
        </div>
      </form>
    </FormProvider>
  )

}

export default EditForm;
